/**
 * /fr/journal-de-decisions — decision-log concept page (French). Wedge.
 * Links to /fr/prd-document-exigences-produit.
 */
import { MarketingShell } from './MarketingShell';

export function FrJournalDecisions() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/journal-de-decisions"
      hreflangLocales={['fr']}
      documentTitle="Journal de décisions (decision log) : pourquoi et comment · Draftlize"
      documentDescription="Un journal de décisions garde le « pourquoi » de chaque choix produit. La plupart pourrissent : écrits une fois, jamais relus. Comment en faire un vivant, avec Draftlize."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Journal de décisions</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.2vw, 52px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            Le journal de décisions qui répond enfin<br />
            <em>à « pourquoi on a fait ça ? »</em>
          </h1>
          <p className="lede">
            Un journal de décisions (decision log) est le registre qui garde, pour chaque choix produit, <em>pourquoi</em> on a décidé ainsi. Des mois plus tard, quand quelqu'un rouvre le même débat, avoir le contexte, les options et les arbitrages sous la main le tranche en une minute. Le hic : la plupart des journaux <em>pourrissent</em> — écrits une fois, jamais relus, périmés en silence. Voici comment le tenir, et le garder vivant.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Que consigner dans un journal de décisions.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>La décision</strong> — ce qui a été décidé, en une phrase.</li>
            <li><strong>Le contexte</strong> — pourquoi il fallait décider maintenant.</li>
            <li><strong>Options et arbitrages</strong> — ce qui a été pesé et pourquoi le reste a été écarté. <em>La partie la plus utile ensuite</em>.</li>
            <li><strong>Responsable et date</strong> — qui et quand.</li>
            <li><strong>Portée</strong> — quelles specs ou décisions sont concernées.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Ce format rejoint l'ADR (journal de décisions d'architecture). Beaucoup de décisions naissent en réunion, puis nourrissent le <a href="/fr/prd-document-exigences-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a> — le journal garde la trace du « pourquoi » derrière chacune.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Pourquoi les journaux pourrissent. <em>Et comment l'éviter.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Un journal de décisions en texte plat commence à s'écarter du réel dès que vous l'écrivez. Une décision plus récente en renverse une ancienne, et le document reste sur la vieille. Chez Draftlize, les décisions sont des « cartes vivantes ».
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Chaque décision est une carte adressable</h3>
            <p>Une décision = une carte, avec son contexte, ses options, sa justification et son responsable, référencée par ID. Au lieu de se perdre dans un document, elle reste pointable.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">La décision renversée passe en « stale »</h3>
            <p>Quand une décision récente en renverse une ancienne, l'ancienne — et les specs qui en dépendent — passent automatiquement en « stale ». On ne re-débat pas ce qui est tranché.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Votre agent IA le lit et l'écrit</h3>
            <p>Le journal devient un contexte que Claude Code ou Cursor lisent via MCP. L'agent rédige la suite en sachant « pourquoi » on a décidé ainsi.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La plupart des journaux de décisions ne sont plus lus dès le lendemain. Alors six mois plus tard, le même débat repart de zéro.
        </blockquote>
        <cite>Gardez chaque décision avec son contexte, et signalez ce qu'elle impacte quand elle change. C'est Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Journal de décisions ou compte rendu de réunion ?', a: 'Le compte rendu est « le relevé de la réunion » ; le journal de décisions est « le registre des décisions ». Extraire seulement les décisions du CR et les accumuler, c\'est ce qui sert vraiment ensuite.' },
            { q: 'Qui doit le tenir ?', a: 'En général la personne responsable de la décision (PM ou lead). L\'essentiel est de garder traçable le « pourquoi » de chaque décision.' },
            { q: 'Quel niveau de détail ?', a: 'Il suffit que la décision, le contexte et les arbitrages soient clairs. Plus que la longueur, ce qui compte est de repérer quand une décision devient obsolète.' },
            { q: 'Est-ce la même chose qu\'un ADR ?', a: 'L\'ADR est un type de journal de décisions technique. Draftlize traite les ADR comme les décisions produit en tant que même « carte vivante ».' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un journal de décisions qui ne pourrit pas <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Faites de chaque décision une carte avec son contexte ; si elle est renversée, elle passe en « stale » toute seule, et votre agent IA la lit. Un journal de décisions vivant, avec Draftlize.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
